import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

const CSS_COLOR_KEYWORDS = new Set(['transparent', 'inherit', 'initial', 'unset', 'currentcolor']);

export function fromContentBuilderColor(
  contentBuilderColor?: IContentBuilderFieldColor | string,
  defaultFallback?: string | IContentBuilderFieldColor,
): string {
  if (typeof contentBuilderColor === 'string') {
    if (contentBuilderColor.trim() || defaultFallback === undefined) {
      return contentBuilderColor;
    }
    return fromContentBuilderColor(undefined, defaultFallback);
  }

  const normalizedType = contentBuilderColor?.type?.toLowerCase();

  if (
    !contentBuilderColor ||
    !contentBuilderColor?.value ||
    !contentBuilderColor?.type ||
    normalizedType === ColorFieldTypesValues.Default
  ) {
    if (!defaultFallback || typeof defaultFallback === 'string') {
      const result =
        defaultFallback?.split('-').length === 2
          ? `var(--ui-color-${defaultFallback})`
          : defaultFallback || '';

      return result;
    }
    return fromContentBuilderColor(defaultFallback);
  }

  if (normalizedType === ColorFieldTypesValues.Palette) {
    return `var(--ui-color-${contentBuilderColor.value})`;
  }

  if (normalizedType === ColorFieldTypesValues.Hex) {
    const hexValue = contentBuilderColor.value.trim();
    if (CSS_COLOR_KEYWORDS.has(hexValue.toLowerCase())) {
      return hexValue;
    }
    return hexValue.startsWith('#') ? hexValue : `#${hexValue}`;
  }

  return '';
}

export function isContentBuilderColorConfigured(
  color?: IContentBuilderFieldColor | string | null,
): boolean {
  if (typeof color === 'string') {
    return color.trim().length > 0;
  }

  if (!color?.type || color.value == null || color.value === '') {
    return false;
  }

  return color.type.toLowerCase() !== ColorFieldTypesValues.Default;
}

/** Returns the first configured color among candidates, or undefined. */
export function coalesceContentBuilderColor(
  ...candidates: (IContentBuilderFieldColor | undefined | null)[]
): IContentBuilderFieldColor | undefined {
  for (const candidate of candidates) {
    if (isContentBuilderColorConfigured(candidate)) {
      return candidate ?? undefined;
    }
  }

  return undefined;
}
